import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { InnerPagesService } from 'src/app/pages/common-layout/inner-pages.service';
import { environment } from 'src/environments/environment';

interface RoutePermission {
  permission: boolean;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private _router: Router,
    private _cS: CommonService,
    private activatedRoute: ActivatedRoute,
    private _iS: InnerPagesService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const token = this._cS.getTokenObj();
    const isSiteSelected =
      localStorage.getItem('IsSiteSelected') == 'true' ? true : false;
    document.title =
      (route.data['title'] ? route.data['title'] : 'Login') +
      ' - PayBev Supplier' +
      (environment.apiURL.includes('staging') ? ' Staging' : '');
    const uuid = route.paramMap.get('supplierId');

    if (token != null) {
      if (!uuid) {
        this._router.navigate(['/switch-account']);
        return false;
      }
      if (!isSiteSelected) {
        localStorage.setItem('IsSiteSelected', 'true');
      }

      return this.checkModulePermission(state.url, route);
    }

    if (window.location.href.includes('?t=')) {
      return true;
    }
    this._router.navigate(['/login']);
    return false;
  }

  private checkModulePermission(
    url: string,
    route: ActivatedRouteSnapshot
  ): boolean | UrlTree {
    const path = url.split('?')[0];
    if (path.includes('/my-profile')) {
      return true;
    }

    const supplierId = this.findSupplierId(route);
    if (!supplierId || !path.startsWith(`/supplier/${supplierId}`)) {
      return true;
    }

    const base = `/supplier/${supplierId}`;
    const routes = this.getProtectedRoutes(base);
    const matched = routes.find((entry) => path.startsWith(entry.url));
    if (!matched || matched.permission) {
      return true;
    }

    return this._router.parseUrl(this.getLandingUrl(base));
  }

  private getProtectedRoutes(base: string): RoutePermission[] {
    const routes: RoutePermission[] = [
      {
        permission: !!this._iS.canAccessUsers().canViewUsers,
        url: `${base}/users`,
      },
      {
        permission: !!this._iS.canAccessNotifications().canViewNotifications,
        url: `${base}/notifications`,
      },
      {
        permission: !!this._iS.canAccessSummary().canViewSummary,
        url: `${base}/summary`,
      },
      {
        permission: !!this._iS.canAccessActivity().canViewActivity,
        url: `${base}/activity-detailed`,
      },
      {
        permission: !!this._iS.canAccessContentManager().canViewContent,
        url: `${base}/content-manager`,
      },
      {
        permission: !!this._iS.canAccessPrograms().canViewPrograms,
        url: `${base}/programs`,
      },
      {
        permission: !!this._iS.canAccessPrograms().canViewPrograms,
        url: `${base}/brands`,
      },
      {
        permission: !!this._iS.canAccessPrograms().canViewPrograms,
        url: `${base}/faq`,
      },
      {
        permission: !!this._iS.canAccessReports(),
        url: `${base}/report-activity-export`,
      },
    ];

    return routes.sort((a, b) => b.url.length - a.url.length);
  }

  /**
   * First page this role can open on the site (Summary in most cases). Used for permission
   * redirects and the header logo. Falls back to User Profile, which every signed-in user can open.
   */
  getLandingUrl(base: string): string {
    const settingsPages = [
      {
        permission: !!this._iS.canAccessUsers().canViewUsers,
        url: `${base}/users`,
      },
      {
        permission: !!this._iS.canAccessNotifications().canViewNotifications,
        url: `${base}/notifications`,
      },
      {
        permission: !!this._iS.canAccessTutorials(),
        url: `${base}/tutorials`,
      },
    ];
    const settingUrl =
      settingsPages.find((page) => page.permission)?.url ?? `${base}/user-profile/details`;

    const ordered = [
      {
        permission: !!this._iS.canAccessSummary().canViewSummary,
        url: `${base}/summary`,
      },
      {
        permission: !!this._iS.canAccessPrograms().canViewPrograms,
        url: `${base}/programs`,
      },
      {
        permission: !!this._iS.canAccessActivity().canViewActivity,
        url: `${base}/activity-detailed`,
      },
      {
        permission: !!this._iS.canAccessReports(),
        url: `${base}/report-activity-export`,
      },
      {
        permission: settingsPages.some((page) => page.permission),
        url: settingUrl,
      },
    ];

    return ordered.find((page) => page.permission)?.url ?? `${base}/user-profile/details`;
  }

  private findSupplierId(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const supplierId = current.paramMap.get('supplierId');
      if (supplierId) {
        return supplierId;
      }
      current = current.parent;
    }
    return null;
  }
}
